import React from 'react';
import { formatDateFromApi } from './dateTime';

// 공통 타입 정의 (ProjectSidebar 등과 공유)
export interface ProjectDetail {
  id: string;
  code: string;
  name: string;
  /** STANDALONE | PLATFORM | BRAND */
  projectType?: string;
  /** 프로젝트 설정 — selectedProject(localStorage)에 캐시 */
  featureCodes?: string[];
  earnMethod?: string;
  defaultBrandCode?: string;
  description: string;
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'ARCHIVED';
  createdAt: string;
  logo?: string;
}

/** ProjectSidebar 등 — projectDetail 로드 전 placeholder */
export const EMPTY_PROJECT_DETAIL: ProjectDetail = {
  id: '',
  code: '',
  name: '',
  description: '',
  status: 'ACTIVE',
  createdAt: '',
};

// 공통 유틸리티 함수들
export class CommonUtils {
  // 날짜 포맷팅 (API UTC ISO → 브라우저 로컬 캘린더 날짜)
  static formatDate(dateString: string): string {
    try {
      return formatDateFromApi(dateString);
    } catch (error) {
      console.error('날짜 포맷팅 오류:', error);
      return '';
    }
  }

  // 가격 포맷팅
  static formatPrice(price: number): string {
    return new Intl.NumberFormat('ko-KR').format(price);
  }

  // 프로젝트 정보 로드 (localStorage selectedProject)
  static loadProjectDetail(): ProjectDetail | null {
    const savedProject = localStorage.getItem('selectedProject');
    if (savedProject) {
      const parsedProject = JSON.parse(savedProject) as Record<string, unknown>;
      const projectType =
        (typeof parsedProject.projectType === 'string' ? parsedProject.projectType : undefined) ??
        (typeof parsedProject.type === 'string' ? parsedProject.type : undefined);
      const statusRaw =
        typeof parsedProject.status === 'string' ? parsedProject.status.toUpperCase() : 'ACTIVE';
      const status = (
        ['ACTIVE', 'INACTIVE', 'PENDING', 'ARCHIVED'].includes(statusRaw) ? statusRaw : 'ACTIVE'
      ) as ProjectDetail['status'];
      return {
        id: String(parsedProject.id ?? parsedProject.code ?? ''),
        code: String(parsedProject.code ?? ''),
        name: String(parsedProject.name ?? ''),
        projectType,
        featureCodes: Array.isArray(parsedProject.featureCodes)
          ? parsedProject.featureCodes.filter((c): c is string => typeof c === 'string' && c.length > 0)
          : undefined,
        earnMethod:
          typeof parsedProject.earnMethod === 'string'
            ? parsedProject.earnMethod.trim() || undefined
            : undefined,
        defaultBrandCode:
          typeof parsedProject.defaultBrandCode === 'string'
            ? parsedProject.defaultBrandCode.trim() || undefined
            : undefined,
        description: typeof parsedProject.description === 'string' ? parsedProject.description : '',
        status,
        createdAt: typeof parsedProject.createdAt === 'string' ? parsedProject.createdAt : '',
        logo: typeof parsedProject.logo === 'string' ? parsedProject.logo : undefined,
      };
    }
    return null;
  }

  /** selectedProject(localStorage) 부분 갱신 — 프로젝트 설정 저장 후 등 */
  static patchSelectedProject(
    projectCode: string,
    patch: {
      featureCodes?: string[];
      earnMethod?: string;
      defaultBrandCode?: string;
    }
  ): void {
    const savedProject = localStorage.getItem('selectedProject');
    if (!savedProject) return;
    try {
      const parsed = JSON.parse(savedProject) as Record<string, unknown>;
      if (String(parsed.code ?? '') !== projectCode) return;
      if (patch.featureCodes !== undefined) {
        parsed.featureCodes = patch.featureCodes;
      }
      if (patch.earnMethod !== undefined) {
        const method = patch.earnMethod.trim();
        if (method) parsed.earnMethod = method;
        else delete parsed.earnMethod;
      }
      if (patch.defaultBrandCode !== undefined) {
        const code = patch.defaultBrandCode.trim();
        if (code) parsed.defaultBrandCode = code;
        else delete parsed.defaultBrandCode;
      }
      localStorage.setItem('selectedProject', JSON.stringify(parsed));
    } catch (e) {
      console.warn('selectedProject 갱신 실패:', e);
    }
  }

  // 모바일 사이드바 리사이즈 핸들러
  static useMobileSidebarResize(
    setIsMobileSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>
  ) {
    React.useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth >= 1024) {
          setIsMobileSidebarOpen(false);
        }
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [setIsMobileSidebarOpen]);
  }

  // 페이지네이션 핸들러
  static handlePageChange(
    page: number,
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  ) {
    setCurrentPage(page);
  }

  static handlePageSizeChange(
    size: number,
    setPageSize: React.Dispatch<React.SetStateAction<number>>,
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
  ) {
    setPageSize(size);
    setCurrentPage(0);
  }

  // 정렬 핸들러
  static handleSort(
    field: string,
    currentSortField: string,
    currentSortOrder: string,
    setSortField: React.Dispatch<React.SetStateAction<string>>,
    setSortOrder: React.Dispatch<React.SetStateAction<string>>
  ) {
    if (currentSortField === field) {
      setSortOrder(currentSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  }

  // 검색 핸들러
  static handleSearch(
    searchTerm: string,
    setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  ) {
    setSearchTerm(searchTerm);
  }

  static handleStatusFilter(
    status: string,
    setSelectedStatus: React.Dispatch<React.SetStateAction<string>>
  ) {
    setSelectedStatus(status);
  }

  // 뷰 타입 변경 핸들러
  static handleViewTypeChange(
    newViewType: string,
    setViewType: React.Dispatch<React.SetStateAction<string>>,
    location: any,
    navigate: any,
    basePath: string
  ) {
    setViewType(newViewType);
    const urlParams = new URLSearchParams(location.search);
    urlParams.set('view', newViewType);
    navigate(`${basePath}?${urlParams.toString()}`, { replace: true });
  }

  // 체크박스 핸들러 (제네릭)
  static handleSelectAll<T>(
    selectedItems: string[],
    filteredItems: T[],
    setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>,
    getItemCode: (item: T) => string
  ) {
    if (selectedItems.length === filteredItems.length && filteredItems.length > 0) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredItems.map(item => getItemCode(item)));
    }
  }

  static handleSelectItem(
    itemCode: string,
    selectedItems: string[],
    setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>
  ) {
    if (selectedItems.includes(itemCode)) {
      setSelectedItems(selectedItems.filter(code => code !== itemCode));
    } else {
      setSelectedItems([...selectedItems, itemCode]);
    }
  }

  // 상태 배지 생성 (공통)
  static getStatusBadge(status: string, _type: 'product' | 'shop' | 'voucher' | 'membership' = 'product') {
    switch (String(status || '').toUpperCase()) {
      case 'ACTIVE':
        return { text: '활성', color: 'bg-green-100 text-green-800' };
      case 'INACTIVE':
        return { text: '비활성', color: 'bg-gray-100 text-gray-800' };
      case 'PENDING':
        return { text: '대기중', color: 'bg-yellow-100 text-yellow-800' };
      case 'TERMINATED':
        return { text: '종료', color: 'bg-red-100 text-red-800' };
      case 'DELETED':
        return { text: '삭제', color: 'bg-gray-100 text-gray-500' };
      default:
        return { text: status || '—', color: 'bg-gray-100 text-gray-800' };
    }
  }

  // 뱃지 아이콘 및 색상 생성
  static getBadgeIcon(badge: string) {
    switch (badge.toLowerCase()) {
      case 'hot':
        return '🔥';
      case 'best':
        return '⭐';
      case 'cool':
        return '❄️';
      case 'new':
        return '✨';
      default:
        return '🏷️';
    }
  }

  static getBadgeColor(badge: string) {
    switch (badge.toLowerCase()) {
      case 'hot':
        return 'bg-red-100 text-red-800';
      case 'best':
        return 'bg-yellow-100 text-yellow-800';
      case 'cool':
        return 'bg-blue-100 text-blue-800';
      case 'new':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  // 매장 타입 배지 생성
  static getTypeBadge(type: string) {
    const typeMap: { [key: string]: { label: string; color: string; bgColor: string } } = {
      'franchise': { label: '가맹점', color: 'text-blue-800', bgColor: 'bg-blue-100' },
      'company': { label: '직영점', color: 'text-green-800', bgColor: 'bg-green-100' },
      'popup': { label: '팝업', color: 'text-purple-800', bgColor: 'bg-purple-100' },
      'shopping_mall': { label: '쇼핑몰', color: 'text-orange-800', bgColor: 'bg-orange-100' }
    };

    const typeInfo = typeMap[type] || { label: type, color: 'text-gray-800', bgColor: 'bg-gray-100' };
    
    return {
      label: typeInfo.label,
      className: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeInfo.bgColor} ${typeInfo.color}`
    };
  }

  // 삭제 확인 다이얼로그
  static handleDelete(
    itemCode: string,
    itemName: string,
    onDelete?: (code: string) => void
  ) {
    if (window.confirm(`정말로 이 ${itemName}을(를) 삭제하시겠습니까?`)) {
      if (onDelete) {
        onDelete(itemCode);
      } else {
        console.log(`${itemName} 삭제:`, itemCode);
        // TODO: 실제 삭제 API 호출
      }
    }
  }

  // 네트워크 에러 감지 및 사용자 친화적 메시지 변환
  static isNetworkError(error: any): boolean {
    if (!error) return false;
    
    const errorMessage = error.message || error.toString() || '';
    const errorName = error.name || '';
    
    // 네트워크 에러 패턴 확인
    const networkErrorPatterns = [
      'Failed to fetch',
      'ERR_CONNECTION_REFUSED',
      'ERR_CONNECTION_RESET',
      'ERR_NETWORK',
      'ERR_INTERNET_DISCONNECTED',
      'NetworkError',
      'Network request failed',
      'fetch failed',
      'Connection refused',
      'Connection reset',
      'ECONNREFUSED',
      'ECONNRESET'
    ];
    
    // 에러 메시지나 이름에 네트워크 에러 패턴이 포함되어 있는지 확인
    const hasNetworkErrorPattern = networkErrorPatterns.some(pattern => 
      errorMessage.toLowerCase().includes(pattern.toLowerCase()) ||
      errorName.toLowerCase().includes(pattern.toLowerCase())
    );
    
    // TypeError이고 메시지가 fetch 관련인 경우도 네트워크 에러로 간주
    const isFetchTypeError = errorName === 'TypeError' && 
      (errorMessage.includes('fetch') || errorMessage.includes('network'));
    
    return hasNetworkErrorPattern || isFetchTypeError;
  }

  // 네트워크 에러 메시지 변환
  static getNetworkErrorMessage(error: any): string {
    if (this.isNetworkError(error)) {
      return '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.';
    }
    return error?.message || '알 수 없는 오류가 발생했습니다.';
  }

  // HTTP 에러 응답에서 사용자 친화적인 메시지 추출
  static getHttpErrorMessage(statusCode: number, errorData?: any): string {
    // 서버에서 보낸 에러 메시지 우선 추출
    if (errorData) {
      // errors.error 배열 형태의 응답 구조 처리
      if (errorData.errors?.error && Array.isArray(errorData.errors.error) && errorData.errors.error.length > 0) {
        const errorMessages = errorData.errors.error
          .map((err: any) => err.message)
          .filter((msg: string) => msg) // 빈 메시지 제거
          .join(', ');
        if (errorMessages) {
          return errorMessages;
        }
      }
      
      // 직접 message 필드가 있는 경우
      if (errorData.message) {
        return errorData.message;
      }
    }

    // HTTP 상태 코드별 기본 메시지
    switch (statusCode) {
      case 400:
        return '잘못된 요청입니다. 입력한 정보를 확인해주세요.';
      case 401:
        return '인증이 필요합니다. 로그인을 다시 시도해주세요.';
      case 403:
        return '권한이 없습니다. 이 작업을 수행할 권한이 없습니다.';
      case 404:
        return '요청한 리소스를 찾을 수 없습니다.';
      case 409:
        return '이미 존재하는 데이터입니다.';
      case 500:
        return '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      case 503:
        return '서비스를 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.';
      default:
        return `요청 처리 중 오류가 발생했습니다. (상태 코드: ${statusCode})`;
    }
  }
}

/** stats count/* API 응답: Java Map<String, Long> → Record<string, number> */
export function parseApiCountMap(raw: unknown): Record<string, number> {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!key) continue;
    const n = Number(value);
    out[key] = Number.isFinite(n) ? n : 0;
  }
  return out;
}

/** API 래퍼 응답에서 data.counts(Map) 추출 */
export function parseApiCountMapFromResponse(result: unknown): Record<string, number> {
  if (result == null || typeof result !== 'object') return {};
  const data = (result as Record<string, unknown>).data;
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    const counts = (data as Record<string, unknown>).counts;
    if (counts != null) return parseApiCountMap(counts);
    return parseApiCountMap(data);
  }
  return {};
}